import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import type { AuthState, User, Space, AgencyInfo } from '../types';
import { authService, LoginCredentials, RegisterData, RegisterWithPlanData } from '../services/authService';
import { spaceService } from '../services/spaceService';
import { agencyService } from '../services/agencyService';
import { AUTH_LOGOUT_EVENT, getTokenExpirationMs } from '../services/apiClient';
import { Logger } from '../utils/logger';
// V4-TOAST-SERVICE-IMPORT-CYCLE 2026-05-29 — static import (no real circular dep)
import { toastService } from '../services/toastService';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  registerWithPlan: (data: RegisterWithPlanData) => Promise<{ checkoutUrl: string | null; requiresEmailVerification?: boolean }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchSpace: (spaceId: string) => void;
  sessionExpired: boolean;
  /** Compte désactivé / accès révoqué (ex: guest dont le siège a été révoqué).
   * Distinct de `sessionExpired` pour afficher un message dédié sur la page de login. */
  accountDeactivated: boolean;
  /** Compte invité lecture seule (un seul space d'agence, surface UI réduite). */
  isGuest: boolean;
  /** V4-AUTH-WRAPPER-REMOUNT-LOSES-ERROR — distinct de `isLoading` (qui repasse à `true`
   * pendant login()/register(), APRÈS le premier rendu). `AuthWrapper` ne doit bloquer sur
   * un spinner plein écran que tant que le bootstrap initial n'est pas résolu, jamais pendant
   * une tentative de login — sinon `LoginPage`/`SignInForm` est démonté et son état d'erreur
   * local perdu au moment où le formulaire en aurait le plus besoin. */
  hasInitialized: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Token check interval (every 30 seconds)
const TOKEN_CHECK_INTERVAL = 30 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    currentSpace: null,
    availableSpaces: [],
    agency: null,
    isLoading: true,
    isReadOnly: false,
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [accountDeactivated, setAccountDeactivated] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const tokenCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tokenExpirationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggingOutRef = useRef(false);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle logout - clear state and mark session as expired if needed.
  // `reason='account_inactive'` → message dédié « accès révoqué » au lieu de « session expirée ».
  const handleLogout = useCallback((expired: boolean = false, reason?: 'account_inactive') => {
    if (isLoggingOutRef.current) return;  // Guard anti-double-appel (race condition timer/interval)
    isLoggingOutRef.current = true;
    Logger.info('[AuthContext] Handling logout', { expired });
    
    // Clear any pending timers
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
    if (tokenExpirationTimeoutRef.current) {
      clearTimeout(tokenExpirationTimeoutRef.current);
      tokenExpirationTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    
    authService.logout();
    setAuthState({
      user: null,
      currentSpace: null,
      availableSpaces: [],
      agency: null,
      isLoading: false,
      isReadOnly: false,
    });
    
    if (expired) {
      // Message dédié pour un compte désactivé/révoqué, sinon « session expirée » générique.
      if (reason === 'account_inactive') {
        setAccountDeactivated(true);
      } else {
        setSessionExpired(true);
      }
    }
  }, []);

  // Set up token expiration timer
  const setupTokenExpirationTimer = useCallback(() => {
    const token = authService.getToken();
    if (!token) return;
    
    const expirationMs = getTokenExpirationMs(token);
    if (!expirationMs) return;
    
    // Calculate time until expiration (with 5 min buffer to match apiClient)
    const timeUntilExpiry = expirationMs - Date.now() - (5 * 60 * 1000);
    
    if (timeUntilExpiry <= 0) {
      // V4-AUTH-401-FORCE-LOGOUT (2026-07-03) — le refresh_token (cookie httpOnly,
      // 7 jours) peut encore être valide même si l'access_token en mémoire ne l'est
      // plus : tenter un refresh silencieux avant de couper la session.
      Logger.warn('[AuthContext] Token near/at expiry on page load — attempting silent refresh');
      authService.refreshSession().then((refreshed) => {
        if (refreshed) {
          Logger.info('[AuthContext] Page-load silent refresh succeeded, rescheduling timer');
          setupTokenExpirationTimer();
        } else {
          Logger.warn('[AuthContext] Page-load silent refresh failed, logging out silently');
          handleLogout(false);
        }
      });
      return;
    }
    
    Logger.debug('[AuthContext] Token expiration timer scheduled', {
      expiresInMinutes: Math.round(timeUntilExpiry / 1000 / 60),
    });
    
    // Clear any existing timeout
    if (tokenExpirationTimeoutRef.current) {
      clearTimeout(tokenExpirationTimeoutRef.current);
    }
    
    // Avertissement 60s avant l'expiration
    const warningDelay = timeUntilExpiry - 60 * 1000;
    if (warningDelay > 0) {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = setTimeout(() => {
        Logger.info('[AuthContext] Session expiring in 60s — showing warning');
        // V4-TOAST-SERVICE-IMPORT-CYCLE 2026-05-29 — static import (no real cycle)
        toastService.warning?.('Votre session expire dans 60 secondes');
      }, warningDelay);
    }

    // Set timeout to attempt a silent refresh when the token nears expiry, and
    // only logout if the refresh_token cookie itself has expired/is invalid.
    tokenExpirationTimeoutRef.current = setTimeout(async () => {
      Logger.warn('[AuthContext] Token expiration timer triggered — attempting silent refresh');
      const refreshed = await authService.refreshSession();
      if (refreshed) {
        Logger.info('[AuthContext] Expiration timer: silent refresh succeeded, rescheduling');
        setupTokenExpirationTimer();
        return;
      }
      Logger.warn('[AuthContext] Expiration timer: silent refresh failed, logging out');
      handleLogout(true);
    }, timeUntilExpiry);
  }, [handleLogout]);

  const loadUser = useCallback(async () => {
    // Ne pas vérifier isAuthenticated() ici : le token peut être absent en mémoire
    // après un refresh de page tout en restant valide via le cookie httpOnly.
    // On laisse /auth/me valider la session → 401 = non authentifié.

    try {
      Logger.debug('[AuthContext] Loading authenticated user context');
      const user = await authService.getCurrentUser();
      const spaces = await spaceService.getMySpaces();
      
      // Try to load agency info (returns null for solo users)
      let agencyInfo: AgencyInfo | null = null;
      try {
        agencyInfo = await agencyService.getMyAgency();
      } catch {
        // User is not part of any agency — that's normal
      }

      // Restore previously selected space from localStorage, or pick first
      const savedSpaceId = localStorage.getItem('raiso_selected_space');
      let currentSpace: Space | null = null;
      if (savedSpaceId) {
        currentSpace = spaces.find(s => s.id === savedSpaceId) || null;
      }
      if (!currentSpace && spaces.length > 0) {
        currentSpace = spaces[0];
      }

      const isReadOnly = currentSpace?.spaceRole === 'viewer';

      setAuthState({
        user,
        currentSpace,
        availableSpaces: spaces,
        agency: agencyInfo,
        isLoading: false,
        isReadOnly,
      });

      Logger.info('[AuthContext] User context loaded', {
        userId: user.id,
        spacesCount: spaces.length,
        hasAgency: !!agencyInfo,
        currentSpaceId: currentSpace?.id ?? null,
        isReadOnly,
      });

      // V4-AGENCY-1 (2026-05-21) — Garde-fou : pour un compte agence qui ressort
      // sans aucun space (cas legacy ou bug de création), on log un warning pour
      // alerter l'observabilité. Le backend a un safety net (`/spaces/` GET crée
      // un space par défaut), mais s'il n'a pas tenu, on doit le voir ici.
      if (!!agencyInfo && spaces.length === 0) {
        Logger.warn('[AuthContext] Agency user has no spaces — fallback to safety net expected', {
          userId: user.id,
          agencyId: agencyInfo.id,
        });
      }
      
      // Set up token expiration timer after successful load
      setupTokenExpirationTimer();
    } catch (error: unknown) {
      // V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — extract HTTP status sans `any`
      const status = axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
      if (status === 401) {
        // Pas de session active (page fraîche sans cookie valide) — pas d'alerte "session expirée"
        Logger.debug('[AuthContext] No active session found during bootstrap (401)');
        setAuthState(prev => ({ ...prev, isLoading: false, user: null }));
      } else {
        Logger.error('[AuthContext] Failed to load authenticated user context', {
          error: error instanceof Error ? error.message : 'unknown_error',
        });
        handleLogout(true);
      }
    }
  }, [handleLogout, setupTokenExpirationTimer]);

  // Listen for logout events from apiClient
  useEffect(() => {
    const handleLogoutEvent = (event: CustomEvent<{ reason: string }>) => {
      const reason = event.detail.reason;
      Logger.info('[AuthContext] Received logout event', { reason });
      const expired = reason === 'expired' || reason === 'unauthorized' || reason === 'account_inactive';
      handleLogout(expired, reason === 'account_inactive' ? 'account_inactive' : undefined);
    };

    window.addEventListener(AUTH_LOGOUT_EVENT, handleLogoutEvent as EventListener);
    
    return () => {
      window.removeEventListener(AUTH_LOGOUT_EVENT, handleLogoutEvent as EventListener);
    };
  }, [handleLogout]);

  // Periodic token check as fallback
  useEffect(() => {
    const checkToken = async () => {
      if (authState.user && !authService.isAuthenticated()) {
        // V4-AUTH-401-FORCE-LOGOUT (2026-07-03) — même garde-fou que le timer
        // d'expiration : tenter un refresh silencieux avant de couper la session.
        Logger.warn('[AuthContext] Periodic token check detected expiration — attempting silent refresh');
        const refreshed = await authService.refreshSession();
        if (refreshed) {
          Logger.info('[AuthContext] Periodic check: silent refresh succeeded, rescheduling');
          setupTokenExpirationTimer();
          return;
        }
        Logger.warn('[AuthContext] Periodic check: silent refresh failed, logging out');
        handleLogout(true);
      }
    };

    // Only run interval if user is authenticated
    if (authState.user) {
      tokenCheckIntervalRef.current = setInterval(checkToken, TOKEN_CHECK_INTERVAL);
    }

    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
    };
  }, [authState.user, handleLogout, setupTokenExpirationTimer]);

  useEffect(() => {
    loadUser().finally(() => setHasInitialized(true));

    return () => {
      // Cleanup on unmount
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
      if (tokenExpirationTimeoutRef.current) {
        clearTimeout(tokenExpirationTimeoutRef.current);
      }
    };
  }, [loadUser]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    isLoggingOutRef.current = false;  // Reset guard pour permettre le re-login
    setAuthState(prev => ({ ...prev, isLoading: true }));
    setSessionExpired(false);
    try {
      Logger.debug('[AuthContext] Login flow started', { email: credentials.email });
      await authService.login(credentials);
      await loadUser();
      Logger.info('[AuthContext] Login flow completed', { email: credentials.email });
      return true;
    } catch (error) {
      Logger.warn('[AuthContext] Login flow failed', {
        email: credentials.email,
        error: error instanceof Error ? error.message : 'unknown_error',
      });
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [loadUser]);

  const register = useCallback(async (data: RegisterData) => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    try {
      Logger.info('[AuthContext] Registration flow started', { email: data.email });
      await authService.register(data);
      await authService.login({ email: data.email, password: data.password });
      await loadUser();
      Logger.info('[AuthContext] Registration flow completed', { email: data.email });
      return true;
    } catch (error) {
      Logger.error('[AuthContext] Registration flow failed', {
        email: data.email,
        error: error instanceof Error ? error.message : 'unknown_error',
      });
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [loadUser]);

  const registerWithPlan = useCallback(async (data: RegisterWithPlanData): Promise<{ checkoutUrl: string | null; requiresEmailVerification?: boolean }> => {
    try {
      Logger.info('[AuthContext] Registration with plan started', {
        email: data.email,
        planId: data.plan_id,
        billingInterval: data.billing_interval ?? null,
      });
      const result = await authService.registerWithPlan(data);
      if (result.requires_email_verification) {
        Logger.info('[AuthContext] Registration with plan requires email verification', {
          email: data.email,
        });
        return { checkoutUrl: null, requiresEmailVerification: true };
      }
      if (!result.checkout_url) {
        setAuthState(prev => ({ ...prev, isLoading: true }));
        await loadUser();
      }
      Logger.info('[AuthContext] Registration with plan completed', {
        email: data.email,
        hasCheckoutUrl: !!result.checkout_url,
      });
      return { checkoutUrl: result.checkout_url };
    } catch (error) {
      Logger.error('[AuthContext] Registration with plan failed', {
        email: data.email,
        planId: data.plan_id,
        error: error instanceof Error ? error.message : 'unknown_error',
      });
      throw error;
    }
  }, [loadUser]);

  const logout = useCallback(() => {
    handleLogout(false);
  }, [handleLogout]);

  const switchSpace = useCallback((spaceId: string) => {
    const space = authState.availableSpaces.find(s => s.id === spaceId);
    if (space) {
      Logger.info('[AuthContext] Switching active space', { spaceId, role: space.spaceRole });
      localStorage.setItem('raiso_selected_space', spaceId);
      setAuthState(prev => ({
        ...prev,
        currentSpace: space,
        isReadOnly: space.spaceRole === 'viewer',
      }));
    }
  }, [authState.availableSpaces]);

  const value = useMemo(
    () => ({ ...authState, login, register, registerWithPlan, logout, refreshUser: loadUser, switchSpace, sessionExpired, accountDeactivated, isGuest: authState.user?.accountType === 'GUEST', hasInitialized }),
    [authState, login, register, registerWithPlan, logout, loadUser, switchSpace, sessionExpired, accountDeactivated, hasInitialized]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
